import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { API_HOST } from "../../App";

const OrderProductSchema = z.object({
  productName: z.string(),
  productQuantity: z.number(),
  productPrice: z.string(),
  optionName: z.string().optional(),
});

const ClientOrderCartItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    count: z.number(),
    price: z.number(),
    onPremiseDeliveryMethod: z
      .enum(["TABLE_SERVICE", "PICKUP_AT_COUNTER"])
      .nullish(),
  })
  .passthrough();

const OrderSchema = z.object({
  transactionId: z.string().uuid(),
  name: z.string().optional(),
  customerName: z.string().nullish(),
  hasCustomOrderNames: z.boolean().optional(),
  transactionAmount: z.string(),
  table: z.string(),
  tableId: z.string(),
  status: z.enum(["PENDING", "IN_PROGRESS", "COMPLETED", "FAILED"]), // Assuming possible statuses
  ticketStatus: z
    .enum(["PENDING", "IN_PROGRESS", "COMPLETED", "REJECTED"])
    .optional(),
  products: z.array(OrderProductSchema),
  oposOrderId: z.string().nullish(),
  updatedAt: z.string().nullish(),
  isTakeAway: z.boolean().optional(),
  pickupTime: z.string().nullish(),
  clientOrderCart: z.array(ClientOrderCartItemSchema).optional(),
});

export type Order = z.infer<typeof OrderSchema>;

export const orderQueryOptions = ({
  locationId,
  paymentId,
}: {
  locationId?: string;
  paymentId?: string;
}) =>
  queryOptions({
    queryKey: ["restaurants"],
    queryFn: async () => {
      const response = await fetch(
        `${API_HOST}/v1/payments/${locationId}/success/${paymentId}`,
      );
      const data = await response.json();
      return OrderSchema.parse(data);
    },
    enabled: !!locationId && !!paymentId,
    retry: 4,
  });

export const useOrder = ({
  locationId,
  paymentId,
}: {
  locationId?: string;
  paymentId?: string;
}) => {
  return useQuery(
    orderQueryOptions({
      locationId,
      paymentId,
    }),
  );
};
